import React, {useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {SliceStatus, UnsplashImage, WellniteContentArticle} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {isMobile} from 'utils';

import {getWellniteArticlesByProvider} from '../WellniteContent/wellniteContentActions';
import {
  selectWellniteArticles,
  selectWellniteContentStatus,
} from '../WellniteContent/wellniteContentSelectors';

interface ArticleListProps {
  providerId: string;
}

export function ProviderArticleList({
  providerId,
}: ArticleListProps): React.ReactElement {
  const dispatch = useDispatch();
  const articles = useSelector(selectWellniteArticles);
  const isPending =
    useSelector(selectWellniteContentStatus) === SliceStatus.pending;
  const {t} = useTranslation();
  const max = isMobile ? 2 : 3;
  const [isViewAllClicked, setIsViewAllClicked] = useState(false);

  useEffect(() => {
    if (!isPending && providerId) {
      dispatch(getWellniteArticlesByProvider({providerId}));
    }
  }, [providerId]);

  return (
    <section className="text-left @container mb-6 md:mb-12">
      <div className="flex flex-row justify-between items-center">
        <h2 className="text-[22px] font-montserrat pt-4 pb-4 font-semibold">
          {t('wellniteContent.directBookingArticlesTitle', 'My Articles')}
        </h2>
        {articles && articles?.length > max && !isViewAllClicked ? (
          <button
            className="font-semibold font-inter cursor-pointer text-sm"
            onClick={() => setIsViewAllClicked(true)}
          >
            {t('wellniteContent.viewAll', 'View All')}
          </button>
        ) : null}
      </div>

      {!isPending && providerId ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {articles?.length ? (
            articles
              ?.slice(0, isViewAllClicked ? articles.length : max)
              ?.map(article => <Article key={article._id} article={article} />)
          ) : (
            <h3 className="col-span-full flex justify-center items-center min-h-[100px]">
              {t('wellniteContent.noAvailableArticles', 'No Article available')}
            </h3>
          )}
        </div>
      ) : (
        <ArticlesSkeletonLoader />
      )}
    </section>
  );
}

interface ArticleProps {
  article: WellniteContentArticle;
}

export function Article({article}: ArticleProps): React.ReactElement {
  const image = article.content?.find(c => c.type === 'image')
    ?.value as UnsplashImage;

  return (
    <article>
      <Link to={`/post/${article.slug}`} className="flex flex-col">
        <span className="inline-block w-full h-[110px] mb-3 flex-shrink-0">
          <img
            className="w-full h-full object-cover rounded-xl"
            src={image.urls.thumb}
            alt={image.alt_description ?? article.title}
          />
        </span>
        <span className="inline-block font-medium mb-1 text-sm font-inter line-clamp-2">
          {article.title}
        </span>
        <span className="inline-block text-[13px] mt-0 font-inter">
          {dayjs(article.createdAt).format('MMMM D, YYYY')}
        </span>
      </Link>
    </article>
  );
}

const ArticlesSkeletonLoader = () => {
  return (
    <div className=" grid grid-cols-3 gap-4">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex flex-col items-center space-y-4">
          <div className="w-full h-[110px] bg-gray-300 rounded-xl animate-pulse"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
};
